import { computed, ref, onUpdated, onMounted, onBeforeMount, watch } from 'vue'

import { replaceFromMap, formatInputAndAdjustCursor, formatInput, replaceNonDecimal, reverseThousandCommaSeparation } from '../../../../foundation/js/utils/input'
import { validateCarPrice, validateDownPayment } from '../../Methods/validation';

import GenericInput from '../../../GenericInput/GenericInput.vue'

export default {
    name: 'NewCarLoanCalculatorInputs',
    components: {
        GenericInput,
    },
    props: {
        labelsInput: {
            type: Object,
            required: true,
        },
        labelsError:{
            type: Object,
            required: true,
        },
        optionsInput: {
            type: Object,
            required: true,
        },
        displayErrors: {
            type: Boolean,
            required: false,
            default: false,
        },
        onChangeCallback: {
            type: Function,
            required: true,
        },
        submit: {
            type: Function,
            required: false,
            default: () => {}
        }
        
    },
    setup(props) {
        // Car price input
        const carPrice = ref('')
        const _carPriceRaw = ref(0)
        const carPriceRaw = computed({
            get: () => _carPriceRaw.value,
            set: (val) => {
                _carPriceRaw.value = parseInt(val, 10) || 0;
            }
        });
        const maxCarPrice = ref(props.optionsInput.maxCarPrice)
        const minCarPrice = ref(props.optionsInput.minCarPrice)

        // Downpayment input
        const downPayment = ref('')
        const _downPaymentRaw = ref(0);
        const downPaymentRaw = computed({
            get: () => _downPaymentRaw.value,
            set: (val) => {
                _downPaymentRaw.value = parseInt(val, 10) || 0;
            }
        });
        const minDownPayment = computed(() => (Math.ceil(carPriceRaw.value * (props.optionsInput.minDownPaymentPercent / 100))))

        // Loan term
        const loanTerm = ref(props.optionsInput.defaultLoanTerm ?? 0)
        const loanTermClamped = computed({
            get: () => loanTerm.value,
            set: (val) => {
                if (val < props.optionsInput.minLoanTerm) loanTerm.value = props.optionsInput.minLoanTerm;
                else if (val > props.optionsInput.maxLoanTerm) loanTerm.value = props.optionsInput.maxLoanTerm;
                else loanTerm.value = val;
            }
        });

        // Carprice
        const carPriceValidation = () => {
            return validateCarPrice({
                carPrice: carPriceRaw.value,
                downPayment: downPaymentRaw.value,
                minCarPrice: minCarPrice.value,
                maxCarPrice: maxCarPrice.value,
                minLoanAmount: props.optionsInput.minLoanAmount
            });
        };

        const carPriceCallback = (e) => {
            let value = e.target?.value ?? 0
            value = replaceNonDecimal(value)
            value = reverseThousandCommaSeparation(value)

            carPriceRaw.value = value
            carPrice.value = formatInputAndAdjustCursor(e, value, 10)
            
            // Fill downpayment
            downPaymentRaw.value = minDownPayment.value
            downPayment.value = formatInput(minDownPayment.value)
        }

        const carPriceErrorMessage = computed(() => {
            const priceDiff = carPriceRaw.value - downPaymentRaw.value

            if (carPrice.value === downPayment.value) {
                return ''
            } else if ((carPriceRaw.value < minCarPrice.value || carPriceRaw.value === 0) && priceDiff > 0) {
                return props.labelsError.minCarPriceError
            } else if (carPriceRaw.value > maxCarPrice.value) {
                return props.labelsError.maxCarPriceError
            } else if (priceDiff < props.optionsInput.minLoanAmount) {
                return props.labelsError.minLoanAmountError
            }

            return ''
        })


        // Downpayment
        const downPaymentValidation = () => {
            return validateDownPayment({
                carPrice: carPriceRaw.value,
                downPayment: downPaymentRaw.value,
                minDownPayment: minDownPayment.value
            });
        };

        const downPaymentCallback = (e) => {
            let value = e.target?.value ?? 0
            value = replaceNonDecimal(value)
            value = reverseThousandCommaSeparation(value)

            downPaymentRaw.value = value
            downPayment.value = formatInputAndAdjustCursor(e, value, 10)
        }

        const downPaymentErrorMessage = computed(() => {

            if (carPrice.value === downPayment.value) {
                return props.labelsError.downPaymentEqualsCarPriceError
            } else if ((downPaymentRaw.value > carPriceRaw.value) & (downPaymentRaw.value > 0)) {
                return props.labelsError.downPaymentTooHighError
            } else if (downPaymentRaw.value < minDownPayment.value) {
                return props.labelsError.downPaymentTooLowError
            }

            return ''
        })

        const onChangeCallback = () => {
            if (props.onChangeCallback) {
                props.onChangeCallback({
                    carPrice: carPriceRaw.value,
                    downPayment: downPaymentRaw.value,
                    loanTerm: loanTerm.value,
                    isFormValid: carPriceValidation() && downPaymentValidation()
                });
            }
        }

        watch([carPriceRaw, downPaymentRaw, loanTerm], () => {
            onChangeCallback();
        });

        onBeforeMount(() => {
            const urlParams = new URLSearchParams(window.location.search);

            if(urlParams.has('cp') && urlParams.has('dp') && urlParams.has('lt')) {
                carPriceRaw.value = urlParams.get('cp')
                carPrice.value = formatInput(carPriceRaw.value)
                downPaymentRaw.value = urlParams.get('dp');
                downPayment.value = formatInput(downPaymentRaw.value)
                loanTermClamped.value = urlParams.get('lt');
            }

            onChangeCallback();
        })

        return {
            carPrice,
            downPayment,
            loanTerm,
            loanTermClamped,
            carPriceErrorMessage,
            downPaymentErrorMessage,
            carPriceValidation,
            downPaymentValidation,
            carPriceCallback,
            downPaymentCallback,
        }
    },
}